import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Zap, Users, Target, Star, CheckCircle2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ServiceIcon } from "@/components/ServiceIcon";
import { useCollection } from "@/hooks/useCollection";
import { fileUrl, BACKEND_URL } from "@/lib/api";
import { ASSETS } from "@/lib/assets";

const stats = [
  { label: "Proyek Selesai", value: "150+" },
  { label: "Klien Puas", value: "98%" },
  { label: "Kamera AI", value: "5K+" },
  { label: "Dukungan", value: "24/7" },
];

const values = [
  { icon: ShieldCheck, title: "Aman & Terpercaya", desc: "Keamanan data dan sistem menjadi prioritas utama kami." },
  { icon: Zap, title: "Inovatif & Modern", desc: "Menggunakan teknologi terbaru untuk hasil terbaik." },
  { icon: Users, title: "Tim Profesional", desc: "Didukung tim ahli berpengalaman di bidangnya." },
  { icon: Target, title: "Fokus pada Solusi", desc: "Kami berorientasi pada kebutuhan dan kepuasan klien." },
];

export default function Home() {
  const { items: services } = useCollection("services");
  const { items: products } = useCollection("products");
  const { items: testimonials } = useCollection("testimonials");
  const featured = products.filter((p) => p.featured).slice(0, 3);
  const heroServices = [...services].sort((a, b) => (a.order ?? 99) - (b.order ?? 99)).slice(0, 4);

  return (
    <div className="overflow-hidden -mt-16 lg:-mt-20">
      {/* HERO */}
      <section className="relative min-h-[100vh] flex flex-col justify-center pt-24 lg:pt-28">
        <div className="absolute inset-0 overflow-hidden">
          <img src={ASSETS.heroScene} alt="Lotus Lentera AI" className="absolute inset-0 w-full h-full object-cover animate-kenburns" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#07080B] via-[#07080B]/85 lg:via-[#07080B]/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#07080B] via-transparent to-[#07080B]/40" />

        {/* Living overlay: scanline, pulse rings & floating particles over the hologram */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="scanline" />
          <div className="hidden sm:block absolute" style={{ left: "48%", top: "40%" }}>
            <span className="pulse-ring" style={{ width: 60, height: 60, left: -30, top: -30 }} />
            <span className="pulse-ring" style={{ width: 60, height: 60, left: -30, top: -30, animationDelay: "1.1s" }} />
            <span className="pulse-ring" style={{ width: 60, height: 60, left: -30, top: -30, animationDelay: "2.2s", borderColor: "rgba(212,175,55,0.6)" }} />
          </div>
          {[
            { l: "52%", t: "30%", d: 0, c: "#00F0FF", s: 6 }, { l: "58%", t: "45%", d: 0.6, c: "#D4AF37", s: 5 },
            { l: "46%", t: "52%", d: 1.2, c: "#00F0FF", s: 4 }, { l: "63%", t: "36%", d: 0.3, c: "#D4AF37", s: 6 },
            { l: "55%", t: "60%", d: 1.6, c: "#00F0FF", s: 5 }, { l: "68%", t: "50%", d: 0.9, c: "#F3E5AB", s: 4 },
            { l: "50%", t: "24%", d: 2.0, c: "#D4AF37", s: 5 }, { l: "60%", t: "22%", d: 1.4, c: "#00F0FF", s: 4 },
          ].map((p, i) => (
            <span key={i} className="float-dot hidden sm:block" style={{ left: p.l, top: p.t, width: p.s, height: p.s, background: p.c, boxShadow: `0 0 10px ${p.c}`, animationDelay: `${p.d}s` }} />
          ))}
        </div>


        <div className="relative max-w-[1400px] mx-auto w-full px-4 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="text-xs sm:text-sm tracking-[0.5em] text-white/80 mb-3 font-semibold">WELCOME TO</motion.div>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
              className="font-syne font-black tracking-tight text-5xl sm:text-7xl lg:text-8xl leading-[0.92]">
              <span className="silver-gradient-text block">LOTUS</span>
              <span className="gold-gradient-text block">LENTERA</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-4 text-lg sm:text-2xl font-semibold text-white">Smart Technology for a Brighter Future</motion.p>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-5 text-sm sm:text-base text-white/75 max-w-lg leading-relaxed">
              Kami hadir sebagai mitra teknologi terpercaya untuk membantu bisnis, instansi, dan pemerintah dalam mewujudkan transformasi digital yang efektif, aman dan berkelanjutan.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-8 flex flex-wrap gap-4">
              <Link to="/layanan" className="btn-gold px-8 py-3.5 rounded-full flex items-center gap-2 uppercase tracking-wide text-sm font-bold" data-testid="hero-cta-services">
                Our Services <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/kontak" className="px-8 py-3.5 rounded-full gold-border text-white hover:bg-white/5 transition-colors flex items-center gap-2 uppercase tracking-wide text-sm font-bold" data-testid="hero-cta-contact">
                Contact Us <ArrowRight className="w-4 h-4 text-gold" />
              </Link>
            </motion.div>
            <div className="mt-10 grid grid-cols-4 gap-4 max-w-lg">
              {stats.map((s) => (
                <div key={s.label}>
                  <div className="font-syne font-extrabold text-2xl sm:text-3xl gold-gradient-text">{s.value}</div>
                  <div className="text-[10px] sm:text-xs text-white/50 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="hidden lg:flex absolute right-10 top-1/2 -translate-y-1/2 items-center gap-4">
          <div className="w-16 h-[2px] bg-gradient-to-r from-transparent to-[#D4AF37]" />
          <div className="font-syne font-bold text-lg tracking-[0.15em] text-white/90 vertical-text leading-tight">
            INOVASI TEKNOLOGI<br /><span className="gold-gradient-text">UNTUK MASA DEPAN</span><br />YANG LEBIH BAIK
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="relative bg-[#07080B]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
            <Reveal>
              <div className="text-xs tracking-[0.35em] text-gold uppercase mb-3">Our Services</div>
              <h2 className="font-syne font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white">Solusi Teknologi <span className="gold-gradient-text">Terintegrasi</span></h2>
              <p className="text-white/60 mt-3 max-w-xl text-sm sm:text-base">Kami menyediakan berbagai layanan teknologi digital untuk mendukung pertumbuhan bisnis dan keamanan Anda.</p>
            </Reveal>
            <div className="flex items-center gap-3 text-xs tracking-[0.2em] text-white/50 uppercase">
              <span>Technology</span><span className="text-gold">/</span><span>Security</span><span className="text-gold">/</span><span>Growth</span>
              <div className="hidden sm:block w-20 h-[2px] bg-gradient-to-r from-[#D4AF37] to-transparent" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {heroServices.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.08}>
                <Link to="/layanan" className="group relative block rounded-2xl overflow-hidden gold-border card-hover h-full" data-testid={`service-card-${i}`}>
                  <div className="h-40 overflow-hidden">
                    <img src={fileUrl(s.image)} alt={s.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-transparent to-[#0B0D13]" />
                  </div>
                  <div className="relative bg-[#0B0D13] p-5 -mt-6">
                    <div className="w-11 h-11 rounded-full gold-border bg-[#07080B] flex items-center justify-center text-gold mb-3"><ServiceIcon name={s.icon} className="w-5 h-5" /></div>
                    <h3 className="font-syne font-bold text-white uppercase text-sm tracking-wide">{s.title}</h3>
                    <p className="text-xs text-white/55 mt-2 leading-relaxed line-clamp-3">{s.description}</p>
                    <span className="mt-3 text-gold text-xs font-semibold flex items-center gap-1.5 group-hover:gap-2.5 transition-all">Learn More <ArrowRight className="w-3.5 h-3.5" /></span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>

        {/* VALUE BAR */}
        <div className="border-t border-amber-500/15 bg-[#0B0D13]">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.06}>
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-full gold-border flex items-center justify-center text-gold shrink-0"><v.icon className="w-5 h-5" /></div>
                  <div><h4 className="font-syne font-bold text-white text-sm">{v.title}</h4><p className="text-xs text-white/55 mt-1">{v.desc}</p></div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      {featured.length > 0 && (
        <section className="bg-[#0F1117] border-y border-amber-500/10">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24">
            <Reveal><div className="text-xs tracking-[0.3em] text-gold uppercase mb-3">Produk Unggulan</div>
              <h2 className="font-syne font-extrabold text-3xl sm:text-4xl text-white">Produk <span className="gold-gradient-text">Andalan</span> Kami</h2></Reveal>
            <div className="mt-10 grid md:grid-cols-3 gap-6">
              {featured.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.1}>
                  <div className="bg-ll-card rounded-2xl overflow-hidden card-hover gold-border h-full flex flex-col" data-testid={`product-card-${i}`}>
                    {p.image && <div className="h-48 overflow-hidden relative"><img src={fileUrl(p.image)} alt={p.name} className="w-full h-full object-cover" /><span className="absolute top-3 left-3 px-3 py-1 rounded-full text-[10px] btn-gold font-bold">{p.category}</span></div>}
                    <div className="p-6 flex-1 flex flex-col">
                      <h3 className="font-syne font-semibold text-lg text-white">{p.name}</h3>
                      <p className="text-sm text-white/60 mt-2 flex-1">{p.description}</p>
                      <div className="mt-4 text-gold font-bold font-mono-ll">{p.price}</div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="bg-[#07080B]">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24">
            <Reveal><div className="text-xs tracking-[0.3em] text-gold uppercase mb-3">Testimoni</div>
              <h2 className="font-syne font-extrabold text-3xl sm:text-4xl text-white">Apa Kata <span className="gold-gradient-text">Klien Kami</span></h2></Reveal>
            <div className="mt-10 grid md:grid-cols-3 gap-6">
              {testimonials.slice(0, 3).map((t, i) => (
                <Reveal key={t.id} delay={i * 0.1}>
                  <div className="bg-ll-card rounded-2xl p-7 gold-border h-full flex flex-col">
                    <div className="flex gap-1 mb-4">{Array.from({ length: t.rating || 5 }).map((_, k) => <Star key={k} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />)}</div>
                    <p className="text-white/80 italic flex-1">"{t.quote}"</p>
                    <div className="mt-5 flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full btn-gold flex items-center justify-center font-bold">{t.name?.[0]}</div>
                      <div><div className="text-white font-semibold text-sm">{t.name}</div><div className="text-white/50 text-xs">{t.role} · {t.company}</div></div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24">
        <Reveal>
          <div className="relative rounded-3xl overflow-hidden gold-border p-10 sm:p-16 text-center grid-bg">
            <div className="absolute inset-0 radial-glow" />
            <div className="relative">
              <h2 className="font-syne font-extrabold text-3xl sm:text-4xl text-white">Siap <span className="gold-gradient-text">Transformasi Digital</span> Bisnis Anda?</h2>
              <p className="text-white/60 mt-4 max-w-xl mx-auto">Konsultasikan kebutuhan teknologi Anda dengan tim ahli kami. Gratis, tanpa komitmen.</p>
              <div className="mt-8 flex flex-wrap gap-4 justify-center">
                <Link to="/kontak" className="btn-gold inline-flex items-center gap-2 px-8 py-3.5 rounded-full" data-testid="cta-contact-button">
                  <CheckCircle2 className="w-5 h-5" /> Mulai Konsultasi
                </Link>
                <a href={`${BACKEND_URL}/api/company-profile.pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full gold-border text-white hover:bg-white/5">
                  Download Company Profile
                </a>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
